import React, { useEffect, useRef } from 'react';

import { getApiUrl } from '@/config/api';
import { authManager, AuthSystem } from '@/services/auth/authManager';
import { performAdminForceLogout, storeForceLogoutNotice } from '@/utils/forceLogout';

const HEARTBEAT_INTERVAL_MS = 15_000;
const SESSION_REPLACED_MESSAGE = '此帳號已在另一台設備登入，你已自動登出';

export const AuthSessionMonitor: React.FC = () => {
  const logoutHandledRef = useRef(false);
  const requestInFlightRef = useRef(false);

  useEffect(() => {
    let disposed = false;

    const forceLogout = (message: string) => {
      if (disposed || logoutHandledRef.current) {
        return;
      }
      logoutHandledRef.current = true;
      storeForceLogoutNotice(message);
      performAdminForceLogout();
    };

    const checkSession = async () => {
      const token = authManager.getToken(AuthSystem.ADMIN);
      if (!token || requestInFlightRef.current || logoutHandledRef.current) {
        return;
      }

      requestInFlightRef.current = true;
      try {
        const response = await fetch(getApiUrl('/api/auth/session'), {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        });
        if (response.status !== 401) {
          return;
        }

        // A successful re-login replaces the old server-side session. The old
        // heartbeat can then return 401 after the new token has been stored;
        // that response must not log out the new session.
        if (!authManager.isCurrentToken(AuthSystem.ADMIN, token)) {
          return;
        }

        let body: { code?: string; message?: string } | null = null;
        try {
          body = await response.json();
        } catch {
          // A generic session-ended notice is safer than leaving a stale UI active.
        }

        if (!authManager.isCurrentToken(AuthSystem.ADMIN, token)) {
          return;
        }

        forceLogout(
          body?.code === 'SESSION_REPLACED'
            ? SESSION_REPLACED_MESSAGE
            : body?.message || '登入工作階段已結束，請重新登入',
        );
      } catch {
        // Network failures must not be mistaken for revocation. Normal API
        // error handling and the next heartbeat will retry.
      } finally {
        requestInFlightRef.current = false;
      }
    };

    const intervalId = window.setInterval(checkSession, HEARTBEAT_INTERVAL_MS);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkSession();
      }
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'token' && event.newValue === null) {
        forceLogout('此帳號已在另一個分頁登出');
      } else if (event.key === 'token' && event.newValue) {
        logoutHandledRef.current = false;
        void checkSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorage);
    void checkSession();

    return () => {
      disposed = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return null;
};
